import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Ic } from '../st8n-tickets/kit.jsx';

/**
 * A bottom sheet on a phone, a centred dialog on a desktop (or a side panel with `side`). Escape or a tap outside closes.
 * `dirty`: something typed (or shown once) would be lost, so Escape, the backdrop and the X ask first; `lose` says what.
 * The page may re-render as often as it likes (live updates): the sheet sets up once, and only focuses on opening.
 * Sheets stack (a picker over a ticket's sheet): only the top one answers Escape and keeps Tab.
 */
const open: object[] = [];

/** Moves focus to the next (Shift: previous) control inside `box`, wrapping at the ends, and stops the browser's own Tab:
 *  Safari's Tab skips buttons and links, so from a dialog's last field it would leave for the page behind. A radio group is
 *  one stop (its arrows move inside it), as the browser's own Tab treats it. For any focus trap (a sheet, the ticket panel). */
export function tabWithin(e: { shiftKey: boolean; preventDefault: () => void }, box: HTMLElement) {
  const items = [...box.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((x) => !x.closest('[hidden]') && getComputedStyle(x).visibility !== 'hidden' && getComputedStyle(x).display !== 'none');
  e.preventDefault();
  if (!items.length) { box.focus(); return; }
  const group = (x: HTMLElement) => (x instanceof HTMLInputElement && x.type === 'radio' && x.name ? items.filter((y) => y instanceof HTMLInputElement && y.type === 'radio' && y.name === x.name) as HTMLInputElement[] : null);
  const stops = items.filter((x) => { const g = group(x); return !g || x === (g.find((y) => y === document.activeElement) ?? g.find((y) => y.checked) ?? g[0]); });
  const at = stops.findIndex((x) => x === document.activeElement || x.contains(document.activeElement));
  const next = at === -1 ? (e.shiftKey ? stops.length - 1 : 0) : (at + (e.shiftKey ? -1 : 1) + stops.length) % stops.length;
  stops[next]!.focus();
}

export function Sheet({ title, sub, onClose, children, footer, wide, testId, dirty, lose }: { title: ReactNode; sub?: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean; testId?: string; dirty?: boolean; lose?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const ids = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const dirtyRef = useRef(!!dirty);
  dirtyRef.current = !!dirty;
  const [asking, setAsking] = useState(false);
  const askingRef = useRef(false);
  askingRef.current = asking;
  const request = useCallback(() => {
    if (dirtyRef.current) setAsking(true);
    else closeRef.current();
  }, []);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const me = {};
    open.push(me);
    const k = (e: KeyboardEvent) => {
      if (open[open.length - 1] !== me) return;
      if (e.key === 'Tab') {
        // Keep Tab inside the dialog (the page behind it is not reachable while it is open).
        if (box.current) tabWithin(e, box.current);
        return;
      }
      if (e.key !== 'Escape') return;
      if (askingRef.current) setAsking(false);
      else request();
    };
    addEventListener('keydown', k);
    // Focus the field meant for typing: data-autofocus, else the first text field (not a dropdown above it), else the dialog.
    const el = box.current;
    const first = el?.querySelector<HTMLElement>('[data-autofocus]') ?? el?.querySelector<HTMLElement>('input:not([type=hidden]):not([disabled]), textarea:not([disabled])') ?? el;
    first?.focus({ preventScroll: true });
    return () => {
      removeEventListener('keydown', k);
      open.splice(open.indexOf(me), 1);
      // Closing returns to what opened it (when that is still on the page).
      if (opener && opener !== document.body && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, [request]);
  useEffect(() => {
    if (asking) box.current?.querySelector<HTMLElement>('[data-testid=sheet-keep]')?.focus();
  }, [asking]);
  return (
    <div className="animate-fade fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-[2px] sm:items-center sm:p-6" onMouseDown={request}>
      <div ref={box} role="dialog" aria-modal="true" aria-labelledby={`${ids}-t`} aria-describedby={sub ? `${ids}-s` : undefined} data-testid={testId} tabIndex={-1}
        className={`animate-rise flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-3xl bg-panel shadow-2 outline-none sm:rounded-3xl ${wide ? 'sm:max-w-[680px]' : 'sm:max-w-[520px]'}`}
        onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex justify-center pt-2 sm:hidden" aria-hidden="true"><span className="h-1 w-10 rounded-full bg-line-2" /></div>
        <header className="flex items-start gap-3 px-5 pt-4 pb-3 sm:px-6 sm:pt-5">
          <div className="min-w-0 flex-1">
            <h2 id={`${ids}-t`} className="truncate text-[18px] font-semibold tracking-tight">{title}</h2>
            {sub ? <p id={`${ids}-s`} className="text-[13px] text-dim">{sub}</p> : null}
          </div>
          <button type="button" className="icon-btn -mt-1 -mr-2" onClick={request} aria-label="Close"><Ic.Close width={20} height={20} /></button>
        </header>
        {asking ? (
          <div role="alertdialog" aria-label="Close without saving?" data-testid="sheet-discard" className="mx-5 mb-3 flex flex-wrap items-center gap-2 rounded-2xl bg-warn-soft px-4 py-3 text-[14px] text-warn-text sm:mx-6">
            <span className="min-w-0 flex-1">{lose ?? 'Close and lose what you typed?'}</span>
            <button type="button" className="btn-quiet btn-sm" onClick={() => setAsking(false)} data-testid="sheet-keep">Keep editing</button>
            <button type="button" className="btn-danger btn-sm" onClick={() => closeRef.current()} data-testid="sheet-lose">Close anyway</button>
          </div>
        ) : null}
        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-5 pb-5 sm:px-6">{children}</div>
        {footer ? <footer className="safe-b flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3.5 sm:px-6">{footer}</footer> : null}
      </div>
    </div>
  );
}
